/**
 * 家园互动效果（I 307b，2026-10-03 用户「碰碰睡着的猫、点柜子要有效果」）：动作预设与播放。
 *
 * - 只用 Web Animations API（element.animate）。没有它的环境（jsdom、很旧的浏览器）一律什么都不做，返回 null，不抛错。
 * - 只动 transform / opacity，不碰布局；每个动作都回到原样（不留 fill），不改任何业务状态、不调接口。
 * - 打开了「减少动态效果」时，所有动作都换成一下很轻的淡入淡出（REDUCED）。
 */

export type MotionName =
  | "stir" // 睡着被碰：身子轻轻起伏一下（不醒）
  | "earFlick" // 睡着被碰的第二下：耳朵抖一下（整图小幅转动近似）
  | "nuzzle" // 醒着被碰：蹭一蹭
  | "tiltLeft" // 醒着被碰：朝点的那一侧歪头
  | "tiltRight"
  | "wobble" // 柜子这类立着的家具：轻轻晃一下
  | "knock" // 屋门：咚咚两下
  | "sway" // 地上、台上的植物：从根部摇一摇
  | "swing" // 挂着的东西（挂画、壁灯）：从上面荡一荡
  | "pop"; // 叮嘱小签、相框、菜园门这类小物件：轻轻弹一下

type Preset = { keyframes: Keyframe[]; duration: number; easing: string; origin: string; iterations?: number };

const PRESETS: Record<MotionName, Preset> = {
  stir: {
    keyframes: [
      { transform: "translateY(0) scale(1, 1) rotate(0deg)" },
      { transform: "translateY(2%) scale(1.06, 0.94) rotate(-1.6deg)", offset: 0.32 },
      { transform: "translateY(0) scale(0.985, 1.025) rotate(1deg)", offset: 0.64 },
      { transform: "translateY(0) scale(1, 1) rotate(0deg)" },
    ],
    duration: 1150,
    easing: "ease-in-out",
    origin: "50% 100%",
  },
  earFlick: {
    keyframes: [
      { transform: "rotate(0deg)" },
      { transform: "rotate(-2.2deg)", offset: 0.25 },
      { transform: "rotate(1.4deg)", offset: 0.5 },
      { transform: "rotate(-0.6deg)", offset: 0.75 },
      { transform: "rotate(0deg)" },
    ],
    duration: 380,
    easing: "ease-out",
    origin: "50% 85%",
  },
  nuzzle: {
    keyframes: [
      { transform: "translateX(0) rotate(0deg)" },
      { transform: "translateX(-2%) rotate(-5deg)", offset: 0.25 },
      { transform: "translateX(2%) rotate(4deg)", offset: 0.55 },
      { transform: "translateX(-1%) rotate(-2deg)", offset: 0.8 },
      { transform: "translateX(0) rotate(0deg)" },
    ],
    duration: 720,
    easing: "ease-in-out",
    origin: "50% 100%",
  },
  tiltLeft: {
    keyframes: [{ transform: "rotate(0deg)" }, { transform: "rotate(-7deg)", offset: 0.35 }, { transform: "rotate(-5deg)", offset: 0.7 }, { transform: "rotate(0deg)" }],
    duration: 760,
    easing: "cubic-bezier(.3,.7,.4,1)",
    origin: "50% 90%",
  },
  tiltRight: {
    keyframes: [{ transform: "rotate(0deg)" }, { transform: "rotate(7deg)", offset: 0.35 }, { transform: "rotate(5deg)", offset: 0.7 }, { transform: "rotate(0deg)" }],
    duration: 760,
    easing: "cubic-bezier(.3,.7,.4,1)",
    origin: "50% 90%",
  },
  wobble: {
    keyframes: [
      { transform: "rotate(0deg)" },
      { transform: "rotate(-2.2deg)", offset: 0.2 },
      { transform: "rotate(1.6deg)", offset: 0.45 },
      { transform: "rotate(-0.7deg)", offset: 0.7 },
      { transform: "rotate(0deg)" },
    ],
    duration: 720,
    easing: "ease-out",
    origin: "50% 100%",
  },
  knock: {
    keyframes: [
      { transform: "translateX(0)" },
      { transform: "translateX(-1.2%)", offset: 0.25 },
      { transform: "translateX(1%)", offset: 0.5 },
      { transform: "translateX(-0.5%)", offset: 0.75 },
      { transform: "translateX(0)" },
    ],
    duration: 230,
    easing: "ease-out",
    origin: "50% 50%",
    iterations: 2,
  },
  sway: {
    keyframes: [
      { transform: "skewX(0deg)" },
      { transform: "skewX(7deg)", offset: 0.18 },
      { transform: "skewX(-5.5deg)", offset: 0.42 },
      { transform: "skewX(2.6deg)", offset: 0.65 },
      { transform: "skewX(-0.8deg)", offset: 0.85 },
      { transform: "skewX(0deg)" },
    ],
    duration: 1400,
    easing: "ease-out",
    origin: "50% 100%",
  },
  swing: {
    keyframes: [
      { transform: "rotate(0deg)" },
      { transform: "rotate(4deg)", offset: 0.2 },
      { transform: "rotate(-3deg)", offset: 0.45 },
      { transform: "rotate(1.6deg)", offset: 0.68 },
      { transform: "rotate(-0.6deg)", offset: 0.86 },
      { transform: "rotate(0deg)" },
    ],
    duration: 1500,
    easing: "ease-out",
    origin: "50% 0%",
  },
  pop: {
    keyframes: [{ transform: "scale(1)" }, { transform: "scale(1.07)", offset: 0.35 }, { transform: "scale(0.98)", offset: 0.7 }, { transform: "scale(1)" }],
    duration: 420,
    easing: "ease-out",
    origin: "50% 60%",
  },
};

/** 减少动态效果时的唯一动作：一下很轻的淡入淡出 */
const REDUCED: Preset = {
  keyframes: [{ opacity: 1 }, { opacity: 0.82, offset: 0.5 }, { opacity: 1 }],
  duration: 260,
  easing: "ease-in-out",
  origin: "50% 50%",
};

export const MOTION_NAMES = Object.keys(PRESETS) as MotionName[];

/** 这个动作有多长（毫秒，含重复）；减少动态时是那一下淡入淡出的时长 */
export function motionDuration(name: MotionName, reduced: boolean): number {
  const preset = reduced ? REDUCED : PRESETS[name];
  return preset.duration * (preset.iterations ?? 1);
}

export function canAnimate(el: Element | null | undefined): el is Element & { animate: Element["animate"] } {
  return !!el && typeof (el as { animate?: unknown }).animate === "function";
}

/**
 * 在 el 上播一个动作。返回 Animation（没有 WAAPI 时返回 null）。
 * transform-origin 写在关键帧里（不改元素自己的样式），播完不留痕迹（fill: none）。
 * 同一个元素上一个动作还没播完又来一下：先把上一个停掉，不叠加成乱抖。
 */
export function play(el: Element | null | undefined, name: MotionName, reduced: boolean): Animation | null {
  if (!canAnimate(el)) return null;
  const preset = reduced ? REDUCED : PRESETS[name];
  const running = RUNNING.get(el);
  if (running) running.cancel();
  const keyframes = preset.keyframes.map((frame) => ({ ...frame, transformOrigin: preset.origin }));
  const animation = el.animate(keyframes, {
    duration: preset.duration,
    easing: preset.easing,
    iterations: preset.iterations ?? 1,
    fill: "none",
  });
  RUNNING.set(el, animation);
  const clear = () => {
    if (RUNNING.get(el) === animation) RUNNING.delete(el);
  };
  animation.addEventListener?.("finish", clear);
  animation.addEventListener?.("cancel", clear);
  return animation;
}

const RUNNING = new WeakMap<Element, Animation>();

/** 系统设了「减少动态效果」没有（matchMedia 不在时当没设） */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
